import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { styled } from '@mui/system'
import type { TFunction } from 'i18next'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import { UserAccountListing } from '../../../swagger'
import fullName from '../../../utils/fullName'
import { getCommaSeparatedList } from '../../../utils/getCommaSeparatedList'
import { LoadingContext } from '../../Context/LoadingContext'
import LoadingProgress from '../../Elements/LoadingProgress'
import UserAvatar from '../../Elements/UserAvatar'
import TableHeaders from '../../Interfaces/TableHeaders'
import TableFooterPagination from '../../Pagination/TableFooterPagination'
import NoResultsFound from '../../Table/NoResultsFound'
import TableHeader from '../../Table/TableHeader'

type RolesSummary = {
  hasRoles: boolean
  pendingRoles: Record<string, number>
  currentRoles: Record<string, number>
  recentRoles: Record<string, number>
}

const UserAccountsSection = styled('section')(({ theme }) => ({
  marginBottom: theme.spacing(2),
}))

const ClickableRow = styled(TableRow)({
  '&:hover': {
    cursor: 'pointer',
  },
})

export enum RoleStatus {
  CurrentRoles = 'currentRoles',
  PendingRoles = 'pendingRoles',
}

interface UserAccountsSummaryTableProps {
  userAccounts: UserAccountListing[]
  pageSize: number
  totalCount: number
  handleRowsPerPage: (rowAmount: number) => void
  onPageChange: (newPage: number) => void
}

const firstFooterPage = 0
const baseTen = 10

export const getUserAccountStatus = ({
  isDeactivated,
  isMember,
  t,
}: {
  isDeactivated?: boolean
  isMember?: boolean
  t: TFunction
}): string => {
  if (isDeactivated) {
    return t('UserAccounts.Table.Status.Deactivated', 'Deactivated')
  }

  if (isMember) {
    return t('UserAccounts.Table.Status.Member', 'Member')
  }

  return t('UserAccounts.Table.Status.None', 'None')
}

export const getRoleNamesWithCount = ({
  roles,
  addTrailingSeparator = false,
}: {
  roles: [string, number][]
  addTrailingSeparator?: boolean
}): string[] => {
  return roles.map(([roleName, count], index, items) => {
    const isLast = index === items.length - 1
    const separator = isLast && addTrailingSeparator ? ', ' : ''

    return count > 1
      ? `${roleName}(${count})${separator}`
      : `${roleName}${separator}`
  })
}

export const getAccountsWithRoleSummary = (
  userAccounts: UserAccountListing[],
  t: TFunction
): (UserAccountListing & { status: string; rolesSummary: RolesSummary })[] => {
  return userAccounts.map((row) => {
    const today = new Date()
    return {
      ...row,
      status: getUserAccountStatus({ ...row, t }),
      rolesSummary: row.roles.reduce(
        (acc, role) => {
          acc.hasRoles = true
          const isPending = today < role.validFrom
          const isCurrent = role.validFrom <= today && today < role.validTo
          const isRecent = role.validTo <= today

          if (isPending) {
            if (!acc.pendingRoles[role.name]) {
              acc.pendingRoles[role.name] = 1
              return acc
            }

            acc.pendingRoles[role.name] += 1
            return acc
          }

          if (isCurrent) {
            if (!acc.currentRoles[role.name]) {
              acc.currentRoles[role.name] = 1
              return acc
            }

            acc.currentRoles[role.name] += 1
            return acc
          }

          if (isRecent) {
            if (!acc.recentRoles[role.name]) {
              acc.recentRoles[role.name] = 1
              return acc
            }

            acc.recentRoles[role.name] += 1
            return acc
          }

          return acc
        },
        {
          hasRoles: false,
          pendingRoles: {},
          currentRoles: {},
          recentRoles: {},
        } as RolesSummary
      ),
    }
  })
}

const UserAccountsSummaryTable: React.FC<UserAccountsSummaryTableProps> = ({
  userAccounts,
  pageSize,
  onPageChange,
  totalCount,
  handleRowsPerPage,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const {
    UserAccounts: { fetchUsers: fetchUsersLoadingId },
  } = useLoadingIds()
  const tableHeaders: TableHeaders[] = [
    {
      label: '',
      align: 'left',
      id: 'firstLetterOfLastName',
    },
    {
      label: t('UserAccounts.FullName.Header', 'Full Name'),
      align: 'left',
    },
    {
      label: t('UserAccounts.Email.Header', 'Email'),
      align: 'left',
    },
    {
      label: t('UserAccounts.Roles.Header', 'Roles'),
      align: 'left',
    },
    {
      label: t('UserAccounts.Status.Header', 'Status'),
      align: 'left',
    },
  ]

  const [tableFooterPage, setTableFooterPage] = useState(0)

  const { loadingIds } = React.useContext(LoadingContext)

  useEffect(() => {
    setTableFooterPage(firstFooterPage)
  }, [onPageChange, totalCount])

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    onPageChange(newPage + 1)
    setTableFooterPage(newPage)
  }

  const onChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    handleRowsPerPage(parseInt(event.target.value, baseTen))
  }

  const toUserAccountPage = (userId: number) => {
    navigate({
      pathname: `/admin/user-accounts/${userId}`,
    })
  }

  const userAccountsWithRoleSummary = getAccountsWithRoleSummary(
    userAccounts,
    t
  )

  return (
    <UserAccountsSection
      aria-labelledby={t('UserAccounts.Section.AriaLabel', 'User Accounts')}
    >
      {loadingIds.has(fetchUsersLoadingId) && <LoadingProgress />}

      {!loadingIds.has(fetchUsersLoadingId) && (
        <TableContainer
          component={Paper}
          style={{
            position: 'relative',
          }}
        >
          <Table>
            <TableHead>
              <TableHeader tableHeaders={tableHeaders} />
            </TableHead>
            <TableBody>
              {totalCount === 0 && <NoResultsFound />}

              {userAccountsWithRoleSummary.map((row) => {
                const pendingRoles = Object.entries(
                  row.rolesSummary.pendingRoles
                )
                const currentRoles = Object.entries(
                  row.rolesSummary.currentRoles
                )
                const recentRoles = Object.entries(row.rolesSummary.recentRoles)
                return (
                  <ClickableRow
                    key={row.userKey}
                    onClick={() => toUserAccountPage(row.userKey)}
                  >
                    <TableCell>
                      <UserAvatar
                        familyName={fullName({
                          firstName: row.firstName,
                          lastName: row.lastName,
                        })}
                      />
                    </TableCell>
                    <TableCell color={theme.palette.primary.main}>
                      <Typography variant="body1" component="p">
                        {fullName({
                          firstName: row.firstName,
                          lastName: row.lastName,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell color={theme.palette.primary.main}>
                      <Typography variant="body1" component="p">
                        {row.username}
                      </Typography>
                    </TableCell>
                    <TableCell color={theme.palette.primary.main}>
                      <Typography variant="body1" component="p">
                        {!row.rolesSummary.hasRoles &&
                          t('UserAccountsSummaryTable.Roles.None', 'None')}

                        <span style={{ fontStyle: 'italic' }}>
                          {getCommaSeparatedList(
                            getRoleNamesWithCount({
                              roles: pendingRoles,
                              addTrailingSeparator:
                                currentRoles.length > 0 ||
                                recentRoles.length > 0,
                            })
                          )}
                        </span>

                        <span>
                          {getCommaSeparatedList(
                            getRoleNamesWithCount({
                              roles: currentRoles,
                              addTrailingSeparator: recentRoles.length > 0,
                            })
                          )}
                        </span>

                        <span
                          style={{
                            textDecoration: 'line-through',
                          }}
                        >
                          {getCommaSeparatedList(
                            getRoleNamesWithCount({
                              roles: recentRoles,
                            })
                          )}
                        </span>
                      </Typography>
                    </TableCell>
                    <TableCell color={theme.palette.primary.main}>
                      <Typography variant="body1" component="p">
                        {row.status}
                      </Typography>
                    </TableCell>
                  </ClickableRow>
                )
              })}
            </TableBody>
            {totalCount > pageSize && (
              <TableFooterPagination
                count={totalCount}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={onChangeRowsPerPage}
                page={tableFooterPage}
                rowsPerPage={pageSize}
                colSpan={tableHeaders.length}
              />
            )}
          </Table>
        </TableContainer>
      )}
    </UserAccountsSection>
  )
}

export default UserAccountsSummaryTable
